import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "expenseCard", "rowBody",
    // Summary
    "budgetInput", "approvedFundsInput", "cfisVsCommitted", "totalCommitted", "totalSpent", "totalBalance",
    // Type filter
    "typeFilter",
    // Add commitment modal
    "addCommitmentModal", "addName", "addType", "addPoNum", "addCommittedAmount", "addPoDate",
    // Edit commitment modal
    "editCommitmentModal", "editName", "editType", "editPoNum", "editCommittedAmount", "editPoDate",
    // Delete expense modal
    "deleteExpenseModal",
    // Add row modal
    "addRowModal", "addRowExpenseName", "addRowForm", "addRowInvoiceNum", "addRowInvoiceDate",
    "addRowAmount", "addRowComment", "addRowDropzone", "addRowFileInput", "addRowFileChips",
    "importRowsView", "importRowsTable", "importLink", "addNewLink",
    // Edit row modal
    "editRowModal", "editRowInvoiceNum", "editRowInvoiceDate", "editRowAmount", "editRowComment",
    "editRowDropzone", "editRowFileInput", "editRowFileChips",
    // Delete row modal
    "deleteRowModal",
  ]

  static values = {
    projectId: Number,
    currency: String,
    currencyFormat: String,
    delimiter: String,
    separator: String,
    isOwner: Boolean,
    budgetAmount: Number,
    approvedFunds: Number,
    overallCommitted: Number
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._activeExpenseId = null
    this._activeRowId = null
    this._attachmentIds = []
    this._editAttachmentIds = []
    this._importMode = false
    this._showPendingFlash()
  }

  // ── Number/currency helpers ───────────────────────────────────────

  _parseNumber(str) {
    if (!str) return 0
    let cleaned = str.trim()
    if (this.currencyFormatValue === "european_currency_format") {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      cleaned = cleaned.replace(/'/g, "")
    } else {
      cleaned = cleaned.replace(/,/g, "")
    }
    const val = parseFloat(cleaned)
    return isNaN(val) ? 0 : val
  }

  _formatNumber(num) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    let str
    if (this.currencyFormatValue === "european_currency_format") {
      str = abs.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      str = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      str = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
    return num < 0 ? `-${str}` : str
  }

  _formatCurrency(num) {
    const formatted = this._formatNumber(num)
    return formatted ? `${this.currencyValue}${formatted}` : ""
  }

  // ── Type Filtering ────────────────────────────────────────────────

  filterByType() {
    const selected = this.typeFilterTarget.value
    this.expenseCardTargets.forEach(card => {
      if (selected === "ALL" || card.dataset.expenseType === selected) {
        card.classList.remove("hidden")
      } else {
        card.classList.add("hidden")
      }
    })
  }

  // ── Add Commitment ────────────────────────────────────────────────

  showAddCommitmentModal() {
    this.addNameTarget.value = ""
    if (this.hasAddTypeTarget) this.addTypeTarget.selectedIndex = 0
    this.addPoNumTarget.value = ""
    this.addCommittedAmountTarget.value = ""
    this.addPoDateTarget.value = ""
    this.addCommitmentModalTarget.classList.remove("hidden")
  }

  hideAddCommitmentModal() {
    this.addCommitmentModalTarget.classList.add("hidden")
  }

  async submitAddCommitment() {
    const name = this.addNameTarget.value.trim()
    const committedAmount = this._parseNumber(this.addCommittedAmountTarget.value)
    if (!name) return this._showToast("Name is required", "error")

    const resp = await this._ajax("POST", "create_other_expense", {
      name,
      type: this.hasAddTypeTarget ? this.addTypeTarget.value : "",
      po_number: this.addPoNumTarget.value.trim(),
      committed_amount: committedAmount,
      po_date: this.addPoDateTarget.value || ""
    })

    if (resp?.success) {
      this.hideAddCommitmentModal()
      this._flashAndReload("Commitment created")
    } else {
      this._showToast(resp?.error || "Failed to create commitment", "error")
    }
  }

  // ── Edit Commitment ───────────────────────────────────────────────

  async showEditCommitmentModal(e) {
    const expenseId = e.currentTarget.dataset.expenseId
    this._activeExpenseId = expenseId

    const data = await this._ajax("GET", `get_other_expense_data?expense_id=${expenseId}`)
    if (!data) return

    this.editNameTarget.value = data.name || ""
    if (this.hasEditTypeTarget) {
      const options = this.editTypeTarget.options
      for (let i = 0; i < options.length; i++) {
        if (options[i].value === data.type) { this.editTypeTarget.selectedIndex = i; break }
      }
    }
    this.editPoNumTarget.value = data.po_num || ""
    this.editCommittedAmountTarget.value = data.committed_amount ? this._formatNumber(data.committed_amount) : ""
    this.editPoDateTarget.value = data.po_date || ""
    // Re-init datepicker if flatpickr is present
    if (this.editPoDateTarget._flatpickr && data.po_date) {
      this.editPoDateTarget._flatpickr.setDate(data.po_date, true)
    }
    this.editCommitmentModalTarget.classList.remove("hidden")
  }

  hideEditCommitmentModal() {
    this.editCommitmentModalTarget.classList.add("hidden")
  }

  async submitEditCommitment() {
    const name = this.editNameTarget.value.trim()
    const committedAmount = this._parseNumber(this.editCommittedAmountTarget.value)
    if (!name) return this._showToast("Name is required", "error")

    const resp = await this._ajax("POST", "update_other_expense", {
      expense_id: this._activeExpenseId,
      name,
      type: this.hasEditTypeTarget ? this.editTypeTarget.value : "",
      po_number: this.editPoNumTarget.value.trim(),
      committed_amount: committedAmount,
      po_date: this.editPoDateTarget.value || ""
    })

    if (resp?.success) {
      this.hideEditCommitmentModal()
      this._flashAndReload("Commitment updated")
    } else {
      this._showToast(resp?.error || "Failed to update commitment", "error")
    }
  }

  // ── Delete Commitment ─────────────────────────────────────────────

  showDeleteExpenseConfirm() {
    this.hideEditCommitmentModal()
    this.deleteExpenseModalTarget.classList.remove("hidden")
  }

  hideDeleteExpenseConfirm() {
    this.deleteExpenseModalTarget.classList.add("hidden")
  }

  async confirmDeleteExpense() {
    await this._ajax("DELETE", "delete_other_expense", { expense_id: this._activeExpenseId })
    this.hideDeleteExpenseConfirm()
    this._flashAndReload("Commitment deleted")
  }

  // ── Add Row ───────────────────────────────────────────────────────

  showAddRowModal(e) {
    e.preventDefault()
    this._activeExpenseId = e.currentTarget.dataset.expenseId
    this._attachmentIds = []
    this._importMode = false

    // Set expense name in title
    this.addRowExpenseNameTarget.textContent = e.currentTarget.dataset.expenseName || ""

    // Reset form
    this.addRowInvoiceNumTarget.value = ""
    this.addRowInvoiceDateTarget.value = ""
    this.addRowAmountTarget.value = ""
    this.addRowCommentTarget.value = ""
    this.addRowFileChipsTarget.innerHTML = ""

    // Show form, hide import
    this.addRowFormTarget.classList.remove("hidden")
    this.importRowsViewTarget.classList.add("hidden")
    this.importLinkTarget.classList.remove("hidden")
    this.addNewLinkTarget.classList.add("hidden")

    this.addRowModalTarget.classList.remove("hidden")
  }

  hideAddRowModal() {
    this.addRowModalTarget.classList.add("hidden")
  }

  async submitAddRow() {
    if (this._importMode) {
      // Move selected rows
      const checked = this.importRowsTableTarget.querySelectorAll("input[type='checkbox']:checked")
      if (checked.length === 0) return this._showToast("Select at least one row", "error")
      const rowIds = Array.from(checked).map(cb => cb.dataset.rowId)

      await this._ajax("POST", "move_other_expense_rows", {
        expense_id: this._activeExpenseId,
        rows_to_move: JSON.stringify(rowIds)
      })
      this.hideAddRowModal()
      this._flashAndReload("Rows imported")
      return
    }

    const amount = this._parseNumber(this.addRowAmountTarget.value)
    if (!amount) return this._showToast("Expensed amount is required", "error")

    const resp = await this._ajax("POST", "create_other_expense_row", {
      expense_id: this._activeExpenseId,
      invoice_num: this.addRowInvoiceNumTarget.value.trim(),
      invoice_date: this.addRowInvoiceDateTarget.value || "",
      expensed_amount: amount,
      comment: this.addRowCommentTarget.value.trim(),
      attachment_ids: this._attachmentIds.join(" ")
    })

    if (resp?.success) {
      this.hideAddRowModal()
      this._flashAndReload("Expensed amount added")
    } else {
      this._showToast(resp?.error || "Failed to add row", "error")
    }
  }

  // ── Import/Move Rows ──────────────────────────────────────────────

  async showImportRows(e) {
    e.preventDefault()
    this._importMode = true
    this.addRowFormTarget.classList.add("hidden")
    this.importRowsViewTarget.classList.remove("hidden")
    this.importLinkTarget.classList.add("hidden")
    this.addNewLinkTarget.classList.remove("hidden")

    const resp = await this._ajax("POST", "all_owner_other_expense_rows", {
      expense_id: this._activeExpenseId
    })

    if (resp?.data) {
      let html = '<table class="w-full text-[13px]">'
      html += '<thead><tr class="bg-gray-50 border-b border-gray-200">'
      html += '<th class="px-2 py-1.5 text-left w-8"></th>'
      html += '<th class="px-2 py-1.5 text-left font-medium text-gray-500">Invoice #</th>'
      html += '<th class="px-2 py-1.5 text-left font-medium text-gray-500">Comment</th>'
      html += '<th class="px-2 py-1.5 text-center font-medium text-gray-500">Date</th>'
      html += '<th class="px-2 py-1.5 text-right font-medium text-gray-500">Amount</th>'
      html += '</tr></thead><tbody>'

      if (resp.data.length === 0) {
        html += '<tr><td colspan="5" class="px-2 py-4 text-center text-gray-400 italic">No rows available</td></tr>'
      } else {
        resp.data.forEach(row => {
          html += `<tr class="border-b border-gray-200 hover:bg-gray-50">`
          html += `<td class="px-2 py-1.5"><input type="checkbox" data-row-id="${row.id}" class="w-4 h-4 rounded border-gray-300 text-brand"></td>`
          html += `<td class="px-2 py-1.5">${this._esc(row.invoice_num || "")}</td>`
          html += `<td class="px-2 py-1.5">${this._esc(row.comment || "")}</td>`
          html += `<td class="px-2 py-1.5 text-center">${this._esc(row.invoice_date || "")}</td>`
          html += `<td class="px-0 py-1.5"><div class="flex items-center"><span class="pl-2 text-gray-400 select-none text-[13px]">${this.currencyValue}</span><span class="flex-1 pr-2 text-right">${this._esc(row.expensed_amount || "")}</span></div></td>`
          html += `</tr>`
        })
      }
      html += '</tbody></table>'
      this.importRowsTableTarget.innerHTML = html
    }
  }

  showAddNewRow(e) {
    e.preventDefault()
    this._importMode = false
    this.addRowFormTarget.classList.remove("hidden")
    this.importRowsViewTarget.classList.add("hidden")
    this.importLinkTarget.classList.remove("hidden")
    this.addNewLinkTarget.classList.add("hidden")
  }

  // ── Edit Row ──────────────────────────────────────────────────────

  async showEditRowModal(e) {
    const rowId = e.currentTarget.dataset.rowId
    this._activeRowId = rowId
    this._activeExpenseId = e.currentTarget.dataset.expenseId
    this._editAttachmentIds = []

    const data = await this._ajax("GET", `get_other_expense_row_data?expense_row_id=${rowId}`)
    if (!data) return

    this.editRowInvoiceNumTarget.value = data.invoice_num || ""
    this.editRowInvoiceDateTarget.value = data.invoice_date || ""
    if (this.editRowInvoiceDateTarget._flatpickr && data.invoice_date) {
      this.editRowInvoiceDateTarget._flatpickr.setDate(data.invoice_date, true)
    }
    this.editRowAmountTarget.value = data.expensed_amount ? this._formatNumber(data.expensed_amount) : ""
    this.editRowCommentTarget.value = data.comment || ""

    // Render existing attachments
    this.editRowFileChipsTarget.innerHTML = ""
    if (data.attachments) {
      data.attachments.forEach(att => {
        this._editAttachmentIds.push(att.id)
        this._renderFileChip(this.editRowFileChipsTarget, att.id, att.filename, att.url, true)
      })
    }

    this.editRowModalTarget.classList.remove("hidden")
  }

  hideEditRowModal() {
    this.editRowModalTarget.classList.add("hidden")
  }

  async submitEditRow() {
    const amount = this._parseNumber(this.editRowAmountTarget.value)
    if (!amount) return this._showToast("Expensed amount is required", "error")

    const resp = await this._ajax("POST", "update_other_expense_row", {
      expense_row_id: this._activeRowId,
      invoice_num: this.editRowInvoiceNumTarget.value.trim(),
      invoice_date: this.editRowInvoiceDateTarget.value || "",
      expensed_amount: amount,
      comment: this.editRowCommentTarget.value.trim(),
      attachment_ids: this._editAttachmentIds.join(" ")
    })

    if (resp?.success) {
      this.hideEditRowModal()
      this._flashAndReload("Expensed amount updated")
    } else {
      this._showToast(resp?.error || "Failed to update row", "error")
    }
  }

  // ── Delete Row ────────────────────────────────────────────────────

  showDeleteRowConfirm(e) {
    this._activeRowId = e.currentTarget.dataset.rowId
    this._activeExpenseId = e.currentTarget.dataset.expenseId
    this.deleteRowModalTarget.classList.remove("hidden")
  }

  hideDeleteRowConfirm() {
    this.deleteRowModalTarget.classList.add("hidden")
  }

  async confirmDeleteRow() {
    await this._ajax("DELETE", "delete_other_expense_row", { expense_row_id: this._activeRowId })
    this.hideDeleteRowConfirm()
    this._flashAndReload("Expensed amount deleted")
  }

  // ── Budget ────────────────────────────────────────────────────────

  async saveBudget(event) {
    const input = event.target
    if (input.readOnly) return
    const amount = this._parseNumber(input.value)
    input.value = this._formatNumber(amount)
    if (amount === this.budgetAmountValue) return
    this.budgetAmountValue = amount
    const resp = await this._ajax("POST", "update_other_expense_budget_amount", { budget_amount: amount })
    if (resp?.success) {
      this._showToast("Budget amount updated")
    } else {
      this._showToast("Failed to update budget", "error")
    }
  }

  recalcCfisVsCommitted() {
    const approvedFunds = this._parseNumber(this.approvedFundsInputTarget.value)
    const diff = approvedFunds - this.overallCommittedValue
    this.cfisVsCommittedTarget.innerHTML = `<b>${this._formatNumber(diff)}</b>`
    this.cfisVsCommittedTarget.className = this.cfisVsCommittedTarget.className
      .replace(/text-\[#[0-9a-f]+\]/g, "")
      .trim() + (diff >= 0 ? " text-[#73c11d]" : " text-[#d0011b]")
  }

  async saveApprovedFunds(event) {
    const input = event.target
    if (input.readOnly) return
    const amount = this._parseNumber(input.value)
    input.value = this._formatNumber(amount)
    if (amount === this.approvedFundsValue) return
    this.approvedFundsValue = amount
    const resp = await this._ajax("POST", "update_cfis_approved_funds", { amount })
    if (resp?.success) {
      this._showToast("Approved funds updated")
    } else {
      this._showToast("Failed to update approved funds", "error")
    }
  }

  // ── File Upload (Add Row) ─────────────────────────────────────────

  triggerFileInput(e) {
    e.preventDefault()
    this.addRowFileInputTarget.click()
  }

  onFileSelect(e) {
    Array.from(e.target.files).forEach(file => this._doFileUpload(file, this.addRowFileChipsTarget, false))
    e.target.value = ""
  }

  dragOver(e) {
    e.preventDefault()
    this.addRowDropzoneTarget.classList.add("border-brand", "bg-blue-50")
    this.addRowDropzoneTarget.classList.remove("border-gray-300")
  }

  dragLeave(e) {
    this.addRowDropzoneTarget.classList.remove("border-brand", "bg-blue-50")
    this.addRowDropzoneTarget.classList.add("border-gray-300")
  }

  dropOnZone(e) {
    e.preventDefault()
    this.dragLeave(e)
    Array.from(e.dataTransfer.files).forEach(file => this._doFileUpload(file, this.addRowFileChipsTarget, false))
  }

  // ── File Upload (Edit Row) ────────────────────────────────────────

  triggerEditFileInput(e) {
    e.preventDefault()
    this.editRowFileInputTarget.click()
  }

  onEditFileSelect(e) {
    Array.from(e.target.files).forEach(file => this._doFileUpload(file, this.editRowFileChipsTarget, true))
    e.target.value = ""
  }

  dragOverEdit(e) {
    e.preventDefault()
    this.editRowDropzoneTarget.classList.add("border-brand", "bg-blue-50")
    this.editRowDropzoneTarget.classList.remove("border-gray-300")
  }

  dragLeaveEdit(e) {
    this.editRowDropzoneTarget.classList.remove("border-brand", "bg-blue-50")
    this.editRowDropzoneTarget.classList.add("border-gray-300")
  }

  dropOnZoneEdit(e) {
    e.preventDefault()
    this.dragLeaveEdit(e)
    Array.from(e.dataTransfer.files).forEach(file => this._doFileUpload(file, this.editRowFileChipsTarget, true))
  }

  async _doFileUpload(file, chipsContainer, isEdit) {
    const chip = this._createUploadingChip(file.name)
    chipsContainer.appendChild(chip)

    const formData = new FormData()
    formData.append("file", file)

    try {
      const url = `/projects/${this.projectIdValue}/finances/upload_misc_file`
      const resp = await fetch(url, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken },
        body: formData
      })
      const data = await resp.json()
      if (data.id) {
        if (isEdit) {
          this._editAttachmentIds.push(data.id)
        } else {
          this._attachmentIds.push(data.id)
        }
        chip.remove()
        this._renderFileChip(chipsContainer, data.id, data.filename, data.file_url, isEdit)
      }
    } catch (e) {
      chip.remove()
      this._showToast("Upload failed", "error")
    }
  }

  _createUploadingChip(filename) {
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 px-2 py-1 bg-blue-100 text-blue-700 rounded text-[13px]"
    chip.innerHTML = `<svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> ${this._esc(filename)}`
    return chip
  }

  _renderFileChip(container, id, filename, url, isEdit) {
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 px-2 py-1 bg-gray-100 text-gray-700 rounded text-[13px]"
    chip.dataset.attachmentId = id
    chip.dataset.isEdit = isEdit

    const iconSpan = document.createElement("span")
    iconSpan.innerHTML = '<svg class="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg>'

    const link = document.createElement("a")
    link.href = url
    link.target = "_blank"
    link.className = "hover:underline text-gray-700 truncate max-w-[200px]"
    link.textContent = filename

    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.dataset.action = "click->owner-expenses#removeFileChip"
    removeBtn.className = "text-gray-400 hover:text-red-500 cursor-pointer"
    removeBtn.innerHTML = '<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>'

    chip.appendChild(iconSpan.firstElementChild)
    chip.appendChild(document.createTextNode(" "))
    chip.appendChild(link)
    chip.appendChild(document.createTextNode(" "))
    chip.appendChild(removeBtn)
    container.appendChild(chip)
  }

  removeFileChip(e) {
    const chip = e.currentTarget.closest("[data-attachment-id]")
    if (!chip) return
    const id = parseInt(chip.dataset.attachmentId)
    const isEdit = chip.dataset.isEdit === "true"
    chip.remove()

    if (isEdit) {
      this._editAttachmentIds = this._editAttachmentIds.filter(x => x !== id)
    } else {
      this._attachmentIds = this._attachmentIds.filter(x => x !== id)
    }
  }

  // ── Toast helpers ───────────────────────────────────────────────

  _flashAndReload(message, type = "success") {
    sessionStorage.setItem("owner_expenses_flash", JSON.stringify({ message, type }))
    window.location.reload()
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("owner_expenses_flash")
    if (!raw) return
    sessionStorage.removeItem("owner_expenses_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._showToast(message, type)
    } catch { /* ignore */ }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── AJAX helper ───────────────────────────────────────────────────

  async _ajax(method, action, params = {}) {
    let url = `/projects/${this.projectIdValue}/finances/${action}`

    if (method === "GET") {
      try {
        const resp = await fetch(url, {
          method: "GET",
          headers: { "X-CSRF-Token": this._csrfToken, "Accept": "application/json" }
        })
        if (resp.headers.get("content-type")?.includes("application/json")) {
          return await resp.json()
        }
        return resp.ok
      } catch (e) {
        console.error("Owner expenses request failed:", e)
      }
      return
    }

    const body = new FormData()
    Object.entries(params).forEach(([k, v]) => body.append(k, v))

    const options = {
      method: "POST",
      headers: { "X-CSRF-Token": this._csrfToken },
      body
    }

    if (method !== "POST") body.append("_method", method.toLowerCase())

    try {
      const resp = await fetch(url, options)
      if (resp.headers.get("content-type")?.includes("application/json")) {
        return await resp.json()
      }
      return resp.ok
    } catch (e) {
      console.error("Owner expenses request failed:", e)
    }
  }

  // ── Escape helper ─────────────────────────────────────────────────

  _esc(str) {
    const d = document.createElement("div")
    d.textContent = str
    return d.innerHTML
  }
}
